import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import NumberLineInterval from "../../../../components/visuals/NumberLineInterval";

/* ========== helpers ========== */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

const opToLatex = (op) => (op === "≥" ? "\\ge" : op === "≤" ? "\\le" : op);
const ineqLatex = (op, k) => `x\\,${opToLatex(op)}\\,${k}`;

// oczekiwany obiekt-parsed, jak zwraca parseIntervalInput
function expectedParsed(op, k) {
  if (op === ">")
    return {
      kind: "intervals",
      intervals: [{ left: k, right: Infinity, leftClosed: false, rightClosed: false }],
    };
  if (op === "≥")
    return {
      kind: "intervals",
      intervals: [{ left: k, right: Infinity, leftClosed: true, rightClosed: false }],
    };
  if (op === "<")
    return {
      kind: "intervals",
      intervals: [{ left: -Infinity, right: k, leftClosed: false, rightClosed: false }],
    };
  if (op === "≤")
    return {
      kind: "intervals",
      intervals: [{ left: -Infinity, right: k, leftClosed: false, rightClosed: true }],
    };
  return null;
}

function sameInterval(a, b) {
  if (!a || !b || a.kind !== "intervals" || b.kind !== "intervals") return false;
  if (!Array.isArray(a.intervals) || !Array.isArray(b.intervals)) return false;
  if (a.intervals.length !== 1 || b.intervals.length !== 1) return false;

  const [A] = a.intervals;
  const [B] = b.intervals;

  const eqNum = (x, y) =>
    (x === Infinity && y === Infinity) ||
    (x === -Infinity && y === -Infinity) ||
    (Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) <= 1e-9);

  return (
    eqNum(A.left, B.left) &&
    eqNum(A.right, B.right) &&
    A.leftClosed === B.leftClosed &&
    A.rightClosed === B.rightClosed
  );
}

function intervalRawFor(op, k) {
  if (op === ">") return `(${k}, ∞)`;
  if (op === "≥") return `[${k}, ∞)`;
  if (op === "<") return `(-∞, ${k})`;
  if (op === "≤") return `(-∞, ${k}]`;
  return "";
}

/* ========== generator ========== */
export function generateTask() {
  // tylko jednostronne → przedziały do ±∞
  const op = pick([">", "≥", "<", "≤"]);
  const k = randInt(-9, 9);
  return { type: "InequalityToInfinityTask", data: { op, k } };
}

/* ========== render treści (jedna linia + odstęp pod spodem) ========== */
export function renderTask({ data }) {
  const { op, k } = data;
  return (
    <div style={{ fontSize: 16, lineHeight: 1.5, marginBottom: 12 }}>
      <span>Zapisz nierówność w formie przedziału: </span>
      <span style={{ marginLeft: 8 }}>
        <InlineMath math={ineqLatex(op, k)} />
      </span>
    </div>
  );
}

/* ========== input — jak w Zadaniu 1/2: „x ∈” + IntervalInput ========== */
export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={"x\\;\\in"} />
        </div>
        <IntervalInput
          value={userAnswer}
          onChange={onChange}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 2/3]  lub  [2/3, ∞)"
        />
      </div>
    </div>
  );
}

/* ========== sprawdzanie ========== */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const parsed =
    typeof userAnswer === "string" ? parseIntervalInput(userAnswer) : userAnswer.parsed;

  const expect = expectedParsed(data.op, data.k);
  return sameInterval(parsed, expect);
}

/* ========== prezentacja odpowiedzi użytkownika: x ∈ [przedział] ========== */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || (typeof userAnswer === "object" && !userAnswer.raw)) {
    return <i>(brak odpowiedzi)</i>;
  }
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer.raw;
  return <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />;
}

/* ========== poprawna odpowiedź: x ∈ [przedział] ========== */
export function renderAnswer({ data }) {
  const raw = intervalRawFor(data.op, data.k);
  return <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />;
}

/* ========== wyjaśnienie (Krok 1: reguła + OD RAZU odpowiedź; Krok 2: grafika) ========== */
export function renderExplanation({ data }) {
  const { op, k } = data;

  let raw = "";
  let text = "";
  let dir = "right";   // kierunek zaznaczenia na osi
  let closed = false;  // kropka domknięta?

  if (op === ">") {
    raw = `(${k}, ∞)`;
    text =
      "x > k — bierzemy wartości większe od k, więc zapis to (k, ∞). Po stronie k nawias okrągły; przy ∞ zawsze okrągły.";
    dir = "right"; closed = false;
  } else if (op === "≥") {
    raw = `[${k}, ∞)`;
    text =
      "x ≥ k — bierzemy wartości ≥ k, więc zapis to [k, ∞). Po stronie k nawias domknięty; przy ∞ zawsze okrągły.";
    dir = "right"; closed = true;
  } else if (op === "<") {
    raw = `(-∞, ${k})`;
    text =
      "x < k — bierzemy wartości mniejsze od k, więc zapis to (-∞, k). Po stronie k nawias okrągły; przy -∞ zawsze okrągły.";
    dir = "left"; closed = false;
  } else {
    raw = `(-∞, ${k}]`;
    text =
      "x ≤ k — bierzemy wartości ≤ k, więc zapis to (-∞, k]. Po stronie k nawias domknięty; przy -∞ zawsze okrągły.";
    dir = "left"; closed = true;
  }

  return (
    <div className="space-y-2">
      <p><strong>Krok 1.</strong> {text}</p>
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <BlockMath math={`x\\in\\;${intervalToLatex(raw)}`} />
      </div>

      <p><strong>Krok 2.</strong> Zaznaczamy rozwiązanie na osi liczbowej.</p>
      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        <NumberLineInterval
          t={k}
          tLabelLatex={`${k}`}
          dir={dir}
          closed={closed}
          margin={2.5}
        />
      </div>
    </div>
  );
}
